import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { sendEmail } from "../../api/user";
import {
  MdOutlineEmail,
  MdOutlinePerson,
  MdOutlineMessage,
  MdOutlineBugReport,
  MdOutlineLightbulb,
  MdOutlinePalette,
  MdOutlineSpeed,
  MdOutlineCheckCircle,
} from "react-icons/md";
import {
  RiSendPlaneFill,
  RiChatSmile2Line,
  RiQuestionAnswerLine,
  RiArrowRightLine,
  RiSparklingFill,
} from "react-icons/ri";
import { BiMessageSquareDetail, BiRefresh } from "react-icons/bi";

export default function FeedbackPage() {
  const currUser = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const [feedbackDetails, setFeedbackDetails] = useState({
    name: "",
    email: "",
    category: "General",
    message: "",
  });

  const [isCopyChecked, setIsCopyChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Auto-populate logged-in user information
  useEffect(() => {
    if (isLoggedIn && currUser) {
      setFeedbackDetails((prev) => ({
        ...prev,
        name: prev.name || currUser.name || "",
        email: prev.email || currUser.email || "",
      }));
    }
  }, [isLoggedIn, currUser]);

  const categories = [
    { id: "General", label: "General", icon: RiChatSmile2Line, color: "text-blue-400 border-blue-500/30" },
    { id: "Feature", label: "Feature Idea", icon: MdOutlineLightbulb, color: "text-amber-400 border-amber-500/30" },
    { id: "Bug", label: "Bug Report", icon: MdOutlineBugReport, color: "text-rose-400 border-rose-500/30" },
    { id: "UI/UX", label: "Design / UI", icon: MdOutlinePalette, color: "text-purple-400 border-purple-500/30" },
    { id: "Performance", label: "Performance", icon: MdOutlineSpeed, color: "text-emerald-400 border-emerald-500/30" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeedbackDetails((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const selectCategory = (categoryId) => {
    setFeedbackDetails((prev) => ({ ...prev, category: categoryId }));
  };

  const handleCheckboxChange = () => {
    setIsCopyChecked((prev) => !prev);
  };

  const validateForm = () => {
    const errors = {};

    if (!feedbackDetails.name.trim()) {
      errors.name = "Please enter your name";
    }

    if (!feedbackDetails.email.trim()) {
      errors.email = "Please enter your email address";
    } else if (!/\S+@\S+\.\S+/.test(feedbackDetails.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!feedbackDetails.message.trim()) {
      errors.message = "Please provide your feedback or suggestions";
    } else if (feedbackDetails.message.trim().length < 8) {
      errors.message = "Feedback should be at least 8 characters long";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      try {
        await sendEmail({
          name: feedbackDetails.name,
          email: feedbackDetails.email,
          category: feedbackDetails.category,
          message: feedbackDetails.message,
          copyToSender: isCopyChecked,
        });
      } catch (apiErr) {
        // Log API notice without breaking user confirmation flow
        console.warn("Feedback email endpoint response:", apiErr?.message || apiErr);
      }

      toast.success("Thank you! Your feedback has been received.");
      setIsSubmitted(true);
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setIsSubmitted(false);
    setValidationErrors({});
    setFeedbackDetails({
      name: isLoggedIn && currUser ? currUser.name || "" : "",
      email: isLoggedIn && currUser ? currUser.email || "" : "",
      category: "General",
      message: "",
    });
  };

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      {/* Header Banner */}
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-4 shadow-sm">
          <RiSparklingFill className="w-3.5 h-3.5" />
          <span>Community Feedback</span>
        </div>
        <h1 className="gradient-text text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-3">
          Help Us Shape WIDEO
        </h1>
        <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
          We’re constantly evolving. Share your suggestions, report issues, or tell us what you love most about the platform.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Main Feedback Form / Success State */}
        <div className="lg:col-span-8">
          {isSubmitted ? (
            <div className="glass-panel bg-[#111a2e]/95 border border-slate-700/80 rounded-2xl p-8 sm:p-12 text-center shadow-2xl relative overflow-hidden">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-6 shadow-lg shadow-emerald-500/10">
                <MdOutlineCheckCircle className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-slate-100 mb-2">Feedback Received!</h2>
              <p className="text-slate-400 text-sm max-w-md mx-auto mb-6 leading-relaxed">
                Thank you for taking the time to help improve WIDEO. Our team reviews every piece of feedback to enhance your streaming and creator experience.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition flex items-center gap-2"
                >
                  <BiRefresh className="w-4 h-4" />
                  <span>Send Another Feedback</span>
                </button>
                <Link
                  to="/"
                  className="glow-button px-6 py-2.5 rounded-xl text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2 shadow-lg"
                >
                  <span>Explore Videos</span>
                  <RiArrowRightLine className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className="glass-panel bg-[#111a2e]/90 border border-slate-700/80 rounded-2xl p-6 sm:p-8 shadow-2xl flex flex-col gap-6"
            >
              {/* Category Selection */}
              <div>
                <label className="block text-slate-300 font-semibold mb-2.5 text-xs uppercase tracking-wider">
                  Select Category
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
                  {categories.map((cat) => {
                    const Icon = cat.icon;
                    const isSelected = feedbackDetails.category === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => selectCategory(cat.id)}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition cursor-pointer ${
                          isSelected
                            ? "bg-cyan-500/15 border-cyan-400 text-cyan-300 shadow-md shadow-cyan-500/10 ring-1 ring-cyan-400/40"
                            : "bg-[#0b132b]/80 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200"
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-1.5 ${isSelected ? "text-cyan-300" : cat.color.split(" ")[0]}`} />
                        <span>{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name & Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider">
                    Your Name <span className="text-cyan-400">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <MdOutlinePerson className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={feedbackDetails.name}
                      onChange={handleInputChange}
                      placeholder="e.g. Alex Smith"
                      className={`w-full bg-[#0b132b] text-slate-100 placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 border text-sm font-medium focus:outline-none transition ${
                        validationErrors.name
                          ? "border-rose-500 focus:border-rose-400 focus:ring-1 focus:ring-rose-400/50"
                          : "border-slate-700/80 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50"
                      }`}
                    />
                  </div>
                  {validationErrors.name && (
                    <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider">
                    Email Address <span className="text-cyan-400">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <MdOutlineEmail className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={feedbackDetails.email}
                      onChange={handleInputChange}
                      placeholder="alex@example.com"
                      className={`w-full bg-[#0b132b] text-slate-100 placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 border text-sm font-medium focus:outline-none transition ${
                        validationErrors.email
                          ? "border-rose-500 focus:border-rose-400 focus:ring-1 focus:ring-rose-400/50"
                          : "border-slate-700/80 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50"
                      }`}
                    />
                  </div>
                  {validationErrors.email && (
                    <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.email}</p>
                  )}
                </div>
              </div>

              {/* Message Details */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label htmlFor="message" className="block text-slate-300 font-semibold text-xs uppercase tracking-wider">
                    Your Message <span className="text-cyan-400">*</span>
                  </label>
                  <span className="text-xs text-slate-500">
                    {feedbackDetails.message.length} chars
                  </span>
                </div>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={feedbackDetails.message}
                    onChange={handleInputChange}
                    placeholder="Describe your suggestion, the bug you encountered, or ideas for WIDEO in detail..."
                    className={`w-full bg-[#0b132b] text-slate-100 placeholder-slate-500 rounded-xl p-4 border text-sm font-medium focus:outline-none transition resize-y min-h-[120px] ${
                      validationErrors.message
                        ? "border-rose-500 focus:border-rose-400 focus:ring-1 focus:ring-rose-400/50"
                        : "border-slate-700/80 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50"
                    }`}
                  ></textarea>
                </div>
                {validationErrors.message && (
                  <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.message}</p>
                )}
              </div>

              {/* Copy Checkbox */}
              <div className="flex items-center">
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isCopyChecked}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded border-slate-700 bg-[#0b132b] text-cyan-500 focus:ring-cyan-400 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="text-xs text-slate-400 hover:text-slate-300">
                    Send me a copy of this feedback for my records
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="glow-button w-full py-3.5 rounded-xl text-xs font-bold text-white uppercase tracking-wider transition shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Sending Feedback...</span>
                  </>
                ) : (
                  <>
                    <RiSendPlaneFill className="w-4 h-4" />
                    <span>Submit Feedback</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Side Panel: FAQs & Quick Direct Contacts */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          {/* Card 1: Direct Channels */}
          <div className="glass-panel bg-[#111a2e]/80 border border-slate-700/80 rounded-2xl p-6 shadow-xl">
            <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BiMessageSquareDetail className="w-4 h-4 text-cyan-400" />
              <span>Other Ways to Reach Us</span>
            </h3>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">
              Looking for direct developer contact or urgent support inquiries?
            </p>
            <Link
              to="/contact"
              className="w-full flex items-center justify-between p-3 rounded-xl bg-[#0b132b] border border-slate-800 hover:border-cyan-500/40 text-xs font-semibold text-slate-300 hover:text-cyan-400 transition group"
            >
              <span>Developer Contact Page</span>
              <RiArrowRightLine className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition" />
            </Link>
          </div>

          {/* Card 2: Feedback Commitments */}
          <div className="glass-panel bg-[#111a2e]/80 border border-slate-700/80 rounded-2xl p-6 shadow-xl">
            <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider mb-3 flex items-center gap-2">
              <RiQuestionAnswerLine className="w-4 h-4 text-purple-400" />
              <span>What Happens Next?</span>
            </h3>
            <ul className="space-y-3 text-xs text-slate-400">
              <li className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0"></div>
                <span><strong className="text-slate-300">Direct Review:</strong> Every submission is reviewed by the engineering team.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0"></div>
                <span><strong className="text-slate-300">Feature Roadmap:</strong> Popular feature suggestions get prioritized in upcoming releases.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0"></div>
                <span><strong className="text-slate-300">Bug Fixes:</strong> Reported glitches are isolated and patched quickly.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
